import React, {useState} from 'react';
import Button from '@/components/ui/Button';
import {ModalAlert} from '@/components/alerts';
import axios from 'axios';
import useData from '@/utils/useData';
import CellLoader from '@/components/loading/CellLoader';
import {buildURL} from '@/utils/urlBuilder';
import TableComponent from '@/components/table/Table';
import i18n from 'i18n';
import {useFormattedDateTime} from '@/components/utils/FormattedDateTime';

interface AudioAnonymizationLog {
    id: number;
    createdOn: string;
    eventCallId: number;
    taskId: string;
    status: string;
}

interface AudioAnonymizationLogsTableProps {
    eventCallId: number;
    isAuditView?: boolean;
}

const AudioAnonymizationLogsTable: React.FC<AudioAnonymizationLogsTableProps> = React.memo((
    {
        eventCallId,
        isAuditView = false,
    },
) => {
    const {formatDateTime, isLoaded: configLoaded} = useFormattedDateTime();
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false);
    const [taskToDelete, setTaskToDelete] = useState<number | null>(null);
    const [isDeleting, setIsDeleting] = useState<boolean>(false);
    const [audioAnonymizationLogs, loading] = useData<AudioAnonymizationLog[]>(
        buildURL('audioAnonymizationTasksDetails', {event_call_id: eventCallId}), 'eventDetail', 'setEventCallAudioAnonymizationTasks', 'eventCallAudioAnonymizationTasks',
    );
    

    if (loading) {
        return (
            <CellLoader fullHeight={true} />
        );
    }

    if (audioAnonymizationLogs.length === 0) {
        return (
            <span className="m-3">
                {i18n.t('jt_data_available.text')}
            </span>
        )
    }

    const openDeleteConfirm = (audioAnonymizationTaskId: number): void => {
        setTaskToDelete(audioAnonymizationTaskId);
        setDeleteConfirmOpen(true);
    };

    const closeDeleteConfirm = (): void => {
        setTaskToDelete(null);
        setDeleteConfirmOpen(false);
        setIsDeleting(false);
    };

    const confirmDelete = async (): Promise<void> => {
        if (!taskToDelete) {return;}

        setIsDeleting(true);
        try {
            const url = buildURL('audioAnonymizationTaskDetails', {task_id: taskToDelete});
            await axios.delete(url);
            closeDeleteConfirm();
        } catch (error) {
            setIsDeleting(false);
            throw error;
        }
    };

    const deleteAudioAnonymizationTask = (audioAnonymizationTaskId: number): void => {
        openDeleteConfirm(audioAnonymizationTaskId);
    };

    const columns = [
        !isAuditView && {
            header: i18n.t('th_text_id_column.text'),
            accessorKey: 'id',
        },
        {
            header: i18n.t('th_created.text'),
            accessorKey: 'createdOn',
            cell: (cell: { row: { original: AudioAnonymizationLog } }) => {
                return configLoaded ? formatDateTime(cell.row.original.createdOn, true, {setZone: false},
                ) : '';
            },
        },
        !isAuditView && {
            header: i18n.t('th_event_call.text'),
            accessorKey: 'eventCallId',
        },
        {
            header: i18n.t('th_task.text'),
            accessorKey: 'taskId',
        },
        {
            header: i18n.t('th_status.text'),
            accessorKey: 'status',
        },
        {
            header: i18n.t('th_action.text'),
            cell: (cell: { row: { original: AudioAnonymizationLog } }) => {
                if (cell.row.original.status === 'COMPLETED' || cell.row.original.status === 'ERROR') {
                    return (
                        <div className="d-inline-block">
                            <Button
                                variant="danger"
                                className="d-inline-block"
                                onClick={() => deleteAudioAnonymizationTask(cell.row.original.id)}
                            >
                                {i18n.t('jt_delete.text')}
                            </Button>
                        </div>
                    );
                }

                return null;
            },
        },
    ].filter(Boolean);

    return (
        <>
            <div className="card">
                <div className="card-body">
                    <TableComponent data={audioAnonymizationLogs} columns={columns}/>
                </div>
            </div>
            <ModalAlert
                isOpen={deleteConfirmOpen}
                onClose={closeDeleteConfirm}
                variant="error"
                title={i18n.t('jt_confirm_deletion.text')}
                message={taskToDelete ? i18n.t('jt_task_delete_confirm.text', {taskId: taskToDelete}) : ''}
                actions={[
                    {
                        label: i18n.t('jt_delete.text'),
                        onClick: confirmDelete,
                        variant: 'danger',
                        loading: isDeleting
                    }
                ]}
                dismissible={!isDeleting}
            />
        </>
    );
});


export default AudioAnonymizationLogsTable; 